import React from 'react';

import {Input, Label, Menu} from '@albert-io/atomic';
import {useMenu} from '@albert-io/atomic/organisms/Menu/MenuContext.react';
import {FilterSection, FiltersMenuShell} from 'client/TeacherDashboard/components/FiltersMenu';
import type {SelectOption} from 'client/TeacherDashboard/types';

import {DUE_WITHIN_FILTER_OPTIONS} from './constants';
import type {DueWithinFilter, SkillPathSubjectOption} from './types';

interface Props {
  dueWithin: DueWithinFilter;
  /** Custom-range bounds as YYYY-MM-DD (native date-input format); '' = unset. */
  dueDateStart: string;
  dueDateEnd: string;
  subject: string;
  subjectOptions: SkillPathSubjectOption[];
  onDueWithinChange: (value: DueWithinFilter) => void;
  onDueDateChange: (bound: 'start' | 'end', value: string) => void;
  onSubjectChange: (value: string) => void;
}

const FiltersMenuBody = ({
  dueWithin,
  dueDateStart,
  dueDateEnd,
  subject,
  subjectOptions,
  onDueWithinChange,
  onDueDateChange,
  onSubjectChange
}: Props) => {
  const {close} = useMenu();
  const subjectSelectOptions: ReadonlyArray<SelectOption<string>> = [
    {value: 'all', label: 'All'},
    ...subjectOptions.map((option) => ({value: option.id, label: option.name}))
  ];

  return (
    <>
      <FilterSection
        label='Due Within'
        options={DUE_WITHIN_FILTER_OPTIONS}
        selected={dueWithin}
        onSelect={(value) => {
          onDueWithinChange(value);
          if (value !== 'custom') {
            close();
          }
        }}
      />
      {dueWithin === 'custom' && (
        <div
          className='gap-x1 px-x2 py-x1 flex flex-col'
          onKeyDown={(event) => {
            if (event.key !== 'Escape') {
              event.stopPropagation();
            }
          }}
        >
          <Label className='gap-x0-half text-s flex flex-col'>
            Start
            <Input
              type='date'
              value={dueDateStart}
              max={dueDateEnd || undefined}
              onChange={(e) => onDueDateChange('start', e.target.value)}
            />
          </Label>
          <Label className='gap-x0-half text-s flex flex-col'>
            End
            <Input
              type='date'
              value={dueDateEnd}
              min={dueDateStart || undefined}
              onChange={(e) => onDueDateChange('end', e.target.value)}
            />
          </Label>
        </div>
      )}
      <Menu.Divider />
      <FilterSection
        label='Subject'
        options={subjectSelectOptions}
        selected={subject}
        onSelect={(value) => {
          onSubjectChange(value);
          close();
        }}
      />
    </>
  );
};

export const SkillPathsFiltersMenu = (props: Props) => {
  const isActive = props.dueWithin !== 'schoolYear' || props.subject !== 'all';

  return (
    // No closeOnItemClick: closing is decided per option in FiltersMenuBody so Custom can stay open.
    <FiltersMenuShell data-testid='skill-paths-filters-trigger' isActive={isActive}>
      <FiltersMenuBody {...props} />
    </FiltersMenuShell>
  );
};
